import React, { useEffect, useState } from "react";
import api, { formatTL, formatDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  KeyRound,
  ToggleLeft,
  ToggleRight,
  Pencil,
  X,
  Plus,
  ShieldCheck,
  LogOut,
  Crown,
  UserCog,
  User as UserIcon,
} from "lucide-react";

const ROLE_META = {
  owner: { label: "İşletme Sahibi", icon: Crown, tone: "amber" },
  manager: { label: "Yönetici", icon: UserCog, tone: "indigo" },
  cashier: { label: "Kasiyer", icon: UserIcon, tone: "stone" },
};

export default function Users() {
  const { user: me, isOwner } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);

  const load = () =>
    api
      .get("/users")
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const toggleActive = async (u) => {
    if (u.role === "owner") return toast.error("İşletme Sahibi pasif yapılamaz");
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? "Pasif yapıldı" : "Aktif yapıldı");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşlem başarısız");
    }
  };

  const revoke = async (u) => {
    if (u.role === "owner") return toast.error("İşletme Sahibi oturumları kapatılamaz");
    if (!window.confirm(`${u.name} adlı kullanıcının tüm oturumları kapatılsın mı?`)) return;
    try {
      await api.post(`/users/${u.id}/revoke-sessions`);
      toast.success("Oturumlar kapatıldı");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşlem başarısız");
    }
  };

  const grouped = {
    owner: items.filter((u) => u.role === "owner"),
    manager: items.filter((u) => u.role === "manager"),
    cashier: items.filter((u) => u.role === "cashier"),
  };

  return (
    <div data-testid="users-root">
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono-code text-[10px] tracking-[0.3em] text-stone-500">
            EKİP
          </div>
          <h1 className="font-serif-display text-3xl sm:text-4xl text-stone-900">
            Kullanıcılar
          </h1>
          <p className="text-sm text-stone-500 mt-1">
            {isOwner ? "Tüm ekibi buradan yönetin." : "Kasiyer ekibinizi buradan yönetin."}
          </p>
        </div>
        <button
          data-testid="users-add-btn"
          onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg gold-gradient text-white text-sm"
        >
          <Plus className="w-4 h-4" />
          Yeni Kullanıcı
        </button>
      </div>

      {["owner", "manager", "cashier"].map((role) => {
        const list = grouped[role];
        if (!list.length && role === "owner") return null;
        const meta = ROLE_META[role];
        return (
          <div key={role} className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <meta.icon className="w-4 h-4 text-amber-700" />
              <h2 className="font-serif-display text-xl text-stone-900">{meta.label}</h2>
              <span className="text-xs text-stone-500 font-mono-code">({list.length})</span>
            </div>
            {list.length === 0 ? (
              <div className="text-sm text-stone-500 pl-6">Henüz kayıt yok.</div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {list.map((u) => (
                  <UserCard
                    key={u.id}
                    user={u}
                    me={me}
                    onEdit={() => setEditing(u)}
                    onToggle={() => toggleActive(u)}
                    onReset={() => setResetting(u)}
                    onRevoke={() => revoke(u)}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}

      {loading && <div className="text-stone-500">Yükleniyor…</div>}

      {creating && (
        <CreateUserModal
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); load(); }}
          isOwner={isOwner}
        />
      )}
      {editing && (
        <EditUserModal
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
      {resetting && (
        <ResetPasswordModal
          user={resetting}
          onClose={() => setResetting(null)}
          onSaved={() => { setResetting(null); load(); }}
        />
      )}
    </div>
  );
}

function UserCard({ user: u, me, onEdit, onToggle, onReset, onRevoke }) {
  const isOwner = u.role === "owner";
  const isSelf = u.id === me?.id;
  const isProtected = isOwner || isSelf;
  return (
    <div
      data-testid={`user-card-${u.username || u.email}`}
      className={`card-lux p-5 ${u.active ? "" : "opacity-70"}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="min-w-0">
          <div className="text-xs font-mono-code text-stone-500 uppercase tracking-wider truncate">
            {u.username || u.email}
          </div>
          <div className="font-serif-display text-xl text-stone-900 truncate">
            {u.name}
          </div>
        </div>
        <span
          className={`inline-flex text-[11px] px-2 py-0.5 rounded-full border font-medium ${
            u.active
              ? "bg-emerald-100 text-emerald-800 border-emerald-200"
              : "bg-stone-200 text-stone-600 border-stone-300"
          }`}
        >
          {u.active ? "Aktif" : "Pasif"}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-4">
        <Stat label="Satış" value={u.sales_count} />
        <Stat label="Toplam" value={formatTL(u.sales_total)} />
      </div>
      {u.must_change_password && (
        <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1 mb-3 inline-flex items-center gap-1">
          <ShieldCheck className="w-3 h-3" /> İlk girişte şifre değiştirilecek
        </div>
      )}
      {!isProtected ? (
        <div className="grid grid-cols-2 gap-2">
          <IconBtn testid={`user-edit-${u.username}`} onClick={onEdit} icon={Pencil} label="Düzenle" />
          <IconBtn
            testid={`user-toggle-${u.username}`}
            onClick={onToggle}
            icon={u.active ? ToggleLeft : ToggleRight}
            label={u.active ? "Pasif Yap" : "Aktif Yap"}
            tone={u.active ? "red" : "emerald"}
          />
          <IconBtn testid={`user-reset-${u.username}`} onClick={onReset} icon={KeyRound} label="Şifre Sıfırla" />
          <IconBtn testid={`user-revoke-${u.username}`} onClick={onRevoke} icon={LogOut} label="Oturum Kapat" tone="stone" />
        </div>
      ) : (
        <div className="text-[11px] text-stone-500">
          {isOwner ? "İşletme Sahibi hesabı buradan yönetilemez." : "Kendi hesabınız."}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-stone-200 rounded-lg p-2 bg-stone-50/50">
      <div className="text-[10px] uppercase text-stone-500 tracking-wider">
        {label}
      </div>
      <div className="text-sm font-mono-code text-stone-900">{value}</div>
    </div>
  );
}

function IconBtn({ icon: Icon, label, onClick, tone = "stone", testid }) {
  const tones = {
    stone: "border-stone-200 text-stone-700 hover:bg-stone-50",
    red: "border-red-200 text-red-700 hover:bg-red-50",
    emerald: "border-emerald-200 text-emerald-700 hover:bg-emerald-50",
  };
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg border text-xs ${tones[tone]}`}
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

function CreateUserModal({ onClose, onSaved, isOwner }) {
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState(genTempPassword());
  const [role, setRole] = useState(isOwner ? "cashier" : "cashier");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim() || !username.trim()) return toast.error("İsim ve kullanıcı adı zorunlu");
    if (password.length < 6) return toast.error("Şifre en az 6 karakter olmalı");
    setSaving(true);
    try {
      await api.post("/users", {
        name: name.trim(),
        username: username.trim(),
        password,
        role,
      });
      toast.success(`${role === "manager" ? "Yönetici" : "Kasiyer"} oluşturuldu. İlk girişte şifre değişecek.`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Oluşturma başarısız");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Yeni Kullanıcı" onClose={onClose}>
      <div className="space-y-3">
        <Field label="Rol">
          <select
            data-testid="new-user-role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="input"
          >
            {isOwner && <option value="manager">Yönetici</option>}
            <option value="cashier">Kasiyer</option>
          </select>
        </Field>
        <Field label="Ad Soyad">
          <input
            data-testid="new-user-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="input"
            placeholder="Ör: Ayşe Yılmaz"
          />
        </Field>
        <Field label="Kullanıcı Adı">
          <input
            data-testid="new-user-username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            className="input font-mono-code"
            placeholder="ayse"
          />
        </Field>
        <Field label="Geçici Şifre (kullanıcı ilk girişte değiştirecek)">
          <div className="flex gap-2">
            <input
              data-testid="new-user-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input font-mono-code"
            />
            <button
              type="button"
              onClick={() => setPassword(genTempPassword())}
              className="px-3 py-2 rounded-lg border border-stone-200 text-xs whitespace-nowrap"
            >
              Yenile
            </button>
          </div>
        </Field>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="px-4 py-2 rounded-lg border border-stone-200 text-sm">Vazgeç</button>
        <button
          data-testid="new-user-save"
          onClick={save}
          disabled={saving}
          className="px-4 py-2 rounded-lg gold-gradient text-white text-sm disabled:opacity-60"
        >
          {saving ? "Kaydediliyor…" : "Oluştur"}
        </button>
      </div>
      <FieldStyle />
    </Modal>
  );
}

function EditUserModal({ user: u, onClose, onSaved }) {
  const [name, setName] = useState(u.name || "");
  const [username, setUsername] = useState(u.username || "");
  const [discountLimit, setDiscountLimit] = useState(u.discount_limit_pct ?? "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { name: name.trim(), username: username.trim() };
      if (discountLimit !== "" && !isNaN(Number(discountLimit))) {
        payload.discount_limit_pct = Number(discountLimit);
      }
      await api.patch(`/users/${u.id}`, payload);
      toast.success("Güncellendi");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Güncelleme başarısız");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`${u.name} — Düzenle`} onClose={onClose}>
      <div className="space-y-3">
        <Field label="Ad Soyad">
          <input data-testid="edit-user-name" value={name} onChange={(e) => setName(e.target.value)} className="input" />
        </Field>
        <Field label="Kullanıcı Adı">
          <input data-testid="edit-user-username" value={username} onChange={(e) => setUsername(e.target.value)} className="input font-mono-code" />
        </Field>
        {u.role === "cashier" && (
          <Field label="İndirim Yetkisi (%) — boş bırakırsanız genel limit uygulanır">
            <input
              data-testid="edit-user-discount"
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={discountLimit}
              onChange={(e) => setDiscountLimit(e.target.value)}
              className="input"
              placeholder="Örn: 20"
            />
          </Field>
        )}
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="px-4 py-2 rounded-lg border border-stone-200 text-sm">Vazgeç</button>
        <button data-testid="edit-user-save" onClick={save} disabled={saving} className="px-4 py-2 rounded-lg gold-gradient text-white text-sm disabled:opacity-60">
          {saving ? "Kaydediliyor…" : "Kaydet"}
        </button>
      </div>
      <FieldStyle />
    </Modal>
  );
}

function ResetPasswordModal({ user: u, onClose, onSaved }) {
  const [password, setPassword] = useState(genTempPassword());
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (password.length < 6) return toast.error("Şifre en az 6 karakter olmalı");
    setSaving(true);
    try {
      await api.post(`/users/${u.id}/reset-password`, { new_password: password });
      toast.success("Şifre sıfırlandı. Kullanıcıya iletin — ilk girişte değiştirilecek.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sıfırlama başarısız");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal title={`${u.name} — Şifre Sıfırla`} onClose={onClose}>
      <p className="text-sm text-stone-500 mb-3">
        Kullanıcının tüm oturumları kapanacak ve ilk girişte yeni şifre belirlemesi istenecek.
      </p>
      <Field label="Yeni Geçici Şifre">
        <div className="flex gap-2">
          <input
            data-testid="reset-user-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input font-mono-code"
          />
          <button type="button" onClick={() => setPassword(genTempPassword())} className="px-3 py-2 rounded-lg border border-stone-200 text-xs">Yenile</button>
        </div>
      </Field>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="px-4 py-2 rounded-lg border border-stone-200 text-sm">Vazgeç</button>
        <button data-testid="reset-user-save" onClick={save} disabled={saving} className="px-4 py-2 rounded-lg gold-gradient text-white text-sm disabled:opacity-60">
          {saving ? "Kaydediliyor…" : "Sıfırla"}
        </button>
      </div>
      <FieldStyle />
    </Modal>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase text-stone-500 tracking-widest font-semibold mb-1">
        {label}
      </div>
      {children}
    </label>
  );
}

function FieldStyle() {
  return (
    <style>{`
      .input { width: 100%; padding: 0.6rem 0.75rem; border-radius: 0.5rem; border: 1px solid #e7e5e4; outline: none; background: white; font-size: 0.9rem; }
      .input:focus { border-color: #d4af37; box-shadow: 0 0 0 2px rgba(212,175,55,0.15); }
    `}</style>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md border border-stone-200 p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <div className="font-serif-display text-xl">{title}</div>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-stone-100">
            <X className="w-4 h-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function genTempPassword() {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let out = "";
  for (let i = 0; i < 10; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}
